import { ExternalLink, X } from 'lucide-react';
import type { ReactNode, Ref } from 'react';
import { Link } from 'react-router';
import { useNetworkRequests } from '../../api/queries';
import type { AddressPlan, Attachment, Network } from '../../api/types';
import { IconButton, StatusBadge } from '../../components/ui';
import { formatDateTime, formatRelative } from '../../lib/format';
import { strings } from '../../strings';
import { attachmentPresentation, providerLabel, roleSubnets } from '../shared';
import { RequestStateBadge } from '../requests/parts';
import { AttachmentRecovery } from './AttachmentActions';
import { waitsForNetworkApply, type TopologyItem } from './topology';

const p = strings.networks.page;
const tp = strings.networks.topology;

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="net-panel-section">
      <h3 className="net-panel-heading">{title}</h3>
      {children}
    </section>
  );
}

/**
 * The side panel of the selected tree item (spec 11 6.15.5): a `complementary` region named "<item> details", 360 px on
 * the right (a bottom sheet on phones). Esc in it and Close close it and return focus to the item.
 */
export function SidePanel({
  network,
  item,
  plan,
  dnsLine,
  panelRef,
  onClose,
}: {
  network: Network;
  item: TopologyItem;
  plan: AddressPlan | undefined;
  dnsLine: string;
  panelRef: Ref<HTMLElement>;
  onClose: () => void;
}) {
  const title =
    item.kind === 'attachment'
      ? item.attachment!.deploymentName
      : item.kind === 'region'
        ? item.region!
        : item.kind === 'dns'
          ? p.privateDns
          : item.kind === 'entry-point'
            ? item.entryPoint!.id
            : p.entryPoints;
  return (
    <aside
      ref={panelRef}
      aria-label={p.details(title)}
      tabIndex={-1}
      className="net-panel"
      data-testid="network-side-panel"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="truncate font-mono text-[15px] font-semibold">{title}</h2>
          {item.kind === 'attachment' && (
            <p className="text-[12px] text-fg-muted">
              {p.attachmentContext(
                providerLabel(item.attachment!.provider),
                item.attachment!.region,
                item.attachment!.blueprintName,
              )}
            </p>
          )}
        </div>
        <IconButton label={p.close} onClick={onClose}>
          <X aria-hidden className="size-4" />
        </IconButton>
      </div>
      {item.kind === 'attachment' && <AttachmentDetails network={network} item={item} plan={plan} />}
      {item.kind === 'region' && (
        <p className="mt-3 text-[13px]">
          {p.regionPanel((item.blocks ?? []).join(', ') || strings.common.dash, item.children.length)}
        </p>
      )}
      {item.kind === 'dns' && (
        <div className="mt-3 space-y-2 text-[13px]">
          <p>{dnsLine}</p>
          <p className="text-fg-muted">{p.writtenByNetwork}</p>
          <Link className="link" to={`/networks/${network.id}?tab=dns`}>
            {p.showDns}
          </Link>
        </div>
      )}
      {item.kind === 'entry-point' && (
        <div className="mt-3 space-y-2 text-[13px]">
          <p>
            {[item.entryPoint!.kind, item.entryPoint!.tier, item.entryPoint!.region]
              .filter(Boolean)
              .join(' · ')}
          </p>
          <p>{item.name}</p>
          <Link className="link" to={`/networks/${network.id}?tab=entry-points`}>
            {p.showEntryPoints}
          </Link>
        </div>
      )}
      {item.kind === 'entry-points' && (
        <div className="mt-3 space-y-2 text-[13px]">
          <p>
            {network.settings.entryPoints.length
              ? item.name.split(', ').slice(1).join(', ')
              : p.noEntryPoints}
          </p>
          <Link className="link" to={`/networks/${network.id}?tab=entry-points`}>
            {p.showEntryPoints}
          </Link>
        </div>
      )}
    </aside>
  );
}

/** The attachment's requests in and out (feature spec 17, phase 2), with the Traffic tab's words and a link to it. */
function AttachmentRequests({ network, attachment: a }: { network: Network; attachment: Attachment }) {
  const requests = useNetworkRequests(network.id);
  const enforcing = network.settings.topology.kind === 'micro-segmentation';
  const rows = (requests.data ?? []).flatMap((g) =>
    g.instances
      .filter(
        (i) =>
          !i.withdrawnAt &&
          ((i.sourceDeploymentId === a.deploymentId && i.provider === a.provider && i.region === a.region) ||
            (i.targetDeploymentId === a.deploymentId && i.region === a.region)),
      )
      .map((i) => ({ group: g, instance: i, out: i.sourceDeploymentId === a.deploymentId })),
  );
  return (
    <Section title={tp.requests}>
      {rows.length === 0 ? (
        <p className="text-[13px] text-fg-muted">{tp.noRequests}</p>
      ) : (
        <ul className="space-y-1.5 text-[13px]" data-testid="panel-requests">
          {rows.map(({ group, instance, out }) => (
            <li key={`${instance.sourceDeploymentId}/${group.requestId}/${instance.region}`}>
              <span className="font-mono text-[12.5px]">
                {out
                  ? tp.requestOut(
                      group.requestId,
                      `${instance.targetDeploymentName ?? group.target.blueprintName} / ${(instance.applied ?? instance.proposed)?.shape.targetResourceId ?? group.target.resourceId}`,
                    )
                  : tp.requestIn(
                      group.requestId,
                      `${instance.sourceDeploymentName}/${instance.sourceResourceId}`,
                    )}
              </span>{' '}
              <RequestStateBadge
                state={instance.state}
                enforcement={instance.enforcement}
                enforcing={enforcing}
                decision={instance.applied?.decision ?? instance.proposed?.decision ?? null}
              />
            </li>
          ))}
        </ul>
      )}
      <Link className="link mt-1 inline-block text-[13px]" to={`/networks/${network.id}?tab=traffic`}>
        {tp.openTraffic}
      </Link>
    </Section>
  );
}

function AttachmentDetails({
  network,
  item,
  plan,
}: {
  network: Network;
  item: TopologyItem;
  plan: AddressPlan | undefined;
}) {
  const a = item.attachment!;
  const planned = plan?.regions
    .find((r) => r.provider === a.provider && r.region === a.region)
    ?.attachments.find((x) => x.deploymentId === a.deploymentId);
  const subnets = planned?.subnets.map((s) => ({ role: s.role, cidr: s.cidr })) ?? roleSubnets(a.cidr);
  return (
    <>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-[13px]">
        <StatusBadge presentation={attachmentPresentation(a.state, waitsForNetworkApply(a, network))} />
        {a.lastConnect && (
          <span className="text-fg-muted" title={formatDateTime(a.lastConnect.at)}>
            {p.lastConnect(formatRelative(a.lastConnect.at))} ·{' '}
            <Link className="link" to={`/runs/${a.lastConnect.runId}`}>
              {p.open}
            </Link>
          </span>
        )}
      </div>
      {a.lastError && (
        <p className="mt-2 text-[12.5px]">
          <code className="font-mono">{a.lastError.code}</code>: {a.lastError.message}
        </p>
      )}
      <Section title={p.addresses}>
        <dl className="net-panel-grid">
          <dt>{p.block}</dt>
          <dd className="font-mono">
            {a.cidr} · {a.size}
          </dd>
          {subnets.map((s) => (
            <div key={s.role} className="contents">
              <dt>{s.role}</dt>
              <dd className="font-mono">{s.cidr}</dd>
            </div>
          ))}
        </dl>
      </Section>
      <Section title={p.connectivity}>
        <p className="text-[13px]">{p.connectivityText(a.connectivity)}</p>
      </Section>
      <AttachmentRequests network={network} attachment={a} />
      {a.provider === 'azure' && (
        <Section title={p.grant}>
          <p className="text-[13px]">{p.grantAzure}</p>
        </Section>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <AttachmentRecovery network={network} attachment={a} />
        <Link className="btn" to={`/deployments/${a.deploymentId}`}>
          <ExternalLink aria-hidden className="size-4" />
          {p.openDeployment}
        </Link>
      </div>
    </>
  );
}
